<template>
  <div>
    <header class="topbar">
      <div>
        <div class="topTitle">{{ pageTitle }}</div>
      </div>
      <div class="topRight">
        <el-button
          size="small"
          native-type="button"
          v-if="activeTab === 'monitor' && canAddBehavior"
          v-has="'btn_action_add'"
          @click="openAddBehaviorDialog"
        >
          <span class="topActionPlus">+</span> 添加异常行为配置
        </el-button>
        <el-button
          size="small"
          native-type="button"
          v-else-if="activeTab === 'risk' && canAddRiskLevel"
          v-has="'btn_risk_add'"
          @click="openRiskLevelDialog('create')"
        >
          <span class="topActionPlus">+</span> 添加风险等级
        </el-button>
      </div>
    </header>

    <InterviewerPageTabs v-model="activeTab">
      <el-tab-pane label="异常行为配置" name="monitor">
        <CommonFilterTableCard
          title="📋 异常行为配置"
          :items="monitorConfigs"
          :page.sync="monitorPage.pageNo"
          :page-size.sync="monitorPage.pageSize"
          :use-server-pagination="true"
          :total="monitorTotal"
          :loading="monitorLoading"
          :filter-schema="monitorFilterSchema"
          :filter-values.sync="monitorFilters"
          empty-text="暂无异常行为配置"
          :columns="monitorColumns"
          @page-change="fetchMonitorConfigs"
          @filter-change="onMonitorFilterChange"
        >
          <template #col-behaviorDesc="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ row.behaviorDesc }}</template>
          </template>
          <template #col-riskScore="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ formatRiskScoreText(row) }}</template>
          </template>
          <template #col-weight="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ formatWeightText(row) }}</template>
          </template>
          <template #col-riskCategory="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ formatRiskCategoryText(row) }}</template>
          </template>
          <template #col-sortOrder="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ formatSortOrderText(row) }}</template>
          </template>
          <template #col-actions="{ row }">
            <template v-if="row && !row.__isFiller">
              <div class="row-actions">
                <el-button size="small" native-type="button" v-has="'btn_action_edit'" class="btnLink small" @click="openBehaviorEditDialog(row)">
                  编辑
                </el-button>
                <!-- 删除异常行为配置：需要权限 `btn_action_delete`，点击后调用 `deleteBehaviorConfig(row)` -->
                <!-- <button v-has="'btn_action_delete'" type="button" class="btnLink danger small" @click="deleteBehaviorConfig(row)">
                  删除
                </button> -->
              </div>
            </template>
            <template v-else-if="row && row.__isFiller">&nbsp;</template>
          </template>
          <template #col-remarks="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <span v-else-if="!canEditBehaviorRow(row)" class="remarks-readonly">{{ row.remarks }}</span>
            <el-input
              v-else
              v-model="row.remarks"
              type="textarea"
              :autosize="{ minRows: 2, maxRows: 4 }"
              placeholder="请输入备注"
              size="small"
              @blur="commitMonitorRow(row)"
            />
          </template>
        </CommonFilterTableCard>
      </el-tab-pane>

      <el-tab-pane label="风险等级配置" name="risk">
        <CommonFilterTableCard
          title="📋 风险等级配置"
          :items="riskLevelConfigs"
          :page.sync="riskPage.pageNo"
          :page-size.sync="riskPage.pageSize"
          empty-text="暂无风险等级配置"
          :loading="riskLoading"
          :filter-schema="riskFilterSchema"
          :filter-values.sync="riskFilters"
          :filter-fn="filterRiskLevels"
          :columns="riskColumns"
        >
          <template #col-actions="{ row }">
            <template v-if="row && !row.__isFiller">
              <div class="row-actions">
                <el-button v-if="canEditRiskLevel" v-has="'btn_risk_edit'" size="mini" @click="openRiskLevelDialog('edit', row)">
                  编辑
                </el-button>
                <el-button v-if="canDeleteRiskLevel" v-has="'btn_risk_delete'" size="mini" type="danger" @click="deleteRiskLevel(row)">
                  删除
                </el-button>
              </div>
            </template>
            <template v-else-if="row && row.__isFiller">&nbsp;</template>
          </template>
          <template #col-description="{ row }">
            <template v-if="row && row.__isFiller">&nbsp;</template>
            <template v-else>{{ row.description }}</template>
          </template>
        </CommonFilterTableCard>
      </el-tab-pane>
    </InterviewerPageTabs>

    <!-- 添加/编辑风险等级弹窗 -->
    <el-dialog
      :title="riskDialogMode === 'edit' ? '编辑风险等级' : '添加风险等级'"
      :visible.sync="riskDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="closeRiskDialog"
    >
      <el-form
        ref="riskFormRef"
        :model="riskForm"
        :rules="riskFormRules"
        label-position="top"
        class="dialog-form-sheet"
      >
        <el-form-item label="风险等级" prop="level_name" required>
          <el-input v-model.trim="riskForm.level_name" maxlength="50" placeholder="请输入风险等级名称" />
        </el-form-item>
        <el-form-item label="等级排序" prop="level_code" required>
          <el-input v-model.number="riskForm.level_code" type="number" min="0" :step="1" placeholder="请输入等级排序" />
        </el-form-item>
        <el-form-item label="最小分" prop="min_score" required>
          <el-input v-model.number="riskForm.min_score" type="number" min="0" :step="1" placeholder="请输入最小分" />
        </el-form-item>
        <el-form-item label="最大分" prop="max_score" required>
          <el-input v-model.number="riskForm.max_score" type="number" min="0" :step="1" placeholder="请输入最大分" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model.trim="riskForm.description"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            maxlength="500"
            show-word-limit
            placeholder="请输入描述（可选）"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeRiskDialog">取消</el-button>
        <el-button type="primary" :loading="riskSubmitting" @click="confirmRiskDialog">确认</el-button>
      </span>
    </el-dialog>

    <!-- 编辑异常行为配置弹窗 -->
    <el-dialog
      title="编辑异常行为配置"
      :visible.sync="behaviorEditDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="closeBehaviorEditDialog"
    >
      <el-form
        ref="behaviorEditFormRef"
        :model="behaviorEditForm"
        :rules="behaviorAddFormRules"
        label-position="top"
        class="dialog-form-sheet"
      >
        <el-form-item label="监控项" prop="behaviorDesc" required>
          <el-input
            v-model="behaviorEditForm.behaviorDesc"
            maxlength="60"
            placeholder="请输入监控项名称"
            disabled
            class="behavior-desc-disabled"
          />
        </el-form-item>
        <el-form-item label="分类" prop="riskCategory" required>
          <el-select v-model="behaviorEditForm.riskCategory" placeholder="请选择" style="width: 100%">
            <el-option v-for="c in riskCategoryOptions" :key="c.value" :label="c.label" :value="c.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="风险分数" prop="riskScore" required>
          <el-input
            v-model.number="behaviorEditForm.riskScore"
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="0～100 的整数"
          />
        </el-form-item>
        <el-form-item label="权重" prop="weight" required>
          <el-select v-model="behaviorEditForm.weight" placeholder="请选择" style="width: 100%">
            <el-option v-for="w in weightOptions" :key="w.value" :label="w.label" :value="w.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder" required>
          <el-input v-model.number="behaviorEditForm.sortOrder" type="number" min="0" step="1" placeholder="请输入排序" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="behaviorEditForm.remarks"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            maxlength="500"
            show-word-limit
            placeholder="请输入备注（可选）"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeBehaviorEditDialog">取消</el-button>
        <el-button type="primary" :loading="behaviorEditSubmitting" @click="confirmBehaviorEditDialog">确认</el-button>
      </span>
    </el-dialog>

    <!-- 添加监控项弹窗（仅管理员可添加） -->
    <el-dialog
      title="添加异常行为配置"
      :visible.sync="behaviorAddDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="closeBehaviorAddDialog"
    >
      <el-form
        ref="behaviorAddFormRef"
        :model="behaviorAddForm"
        :rules="behaviorAddFormRules"
        label-position="top"
        class="dialog-form-sheet"
      >
        <el-form-item label="监控项" prop="behaviorDesc" required>
          <el-input v-model="behaviorAddForm.behaviorDesc" maxlength="60" placeholder="请输入监控项名称" />
        </el-form-item>
        <el-form-item label="分类" prop="riskCategory" required>
          <el-select v-model="behaviorAddForm.riskCategory" placeholder="请选择" style="width: 100%">
            <el-option
              v-for="c in riskCategoryOptions"
              :key="c.value"
              :label="c.label"
              :value="c.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="风险分数" prop="riskScore" required>
          <el-input
            v-model.number="behaviorAddForm.riskScore"
            type="number"
            min="0"
            max="100"
            step="1"
            placeholder="0～100 的整数"
          />
        </el-form-item>
        <el-form-item label="权重" prop="weight" required>
          <el-select v-model="behaviorAddForm.weight" placeholder="请选择" style="width: 100%">
            <el-option v-for="w in weightOptions" :key="w.value" :label="w.label" :value="w.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder" required>
          <el-input
            v-model.number="behaviorAddForm.sortOrder"
            type="number"
            min="0"
            step="1"
            placeholder="请输入排序"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="behaviorAddForm.remarks"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            maxlength="500"
            show-word-limit
            placeholder="请输入备注（可选）"
          />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeBehaviorAddDialog">取消</el-button>
        <el-button type="primary" :loading="behaviorAddSubmitting" @click="confirmBehaviorAddDialog">
          确认
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import CommonFilterTableCard from "../components/CommonFilterTableCard.vue";
import InterviewerPageTabs from "@/components/interviewer/InterviewerPageTabs.vue";
import {
  listRiskItems,
  addBehaviorConfig,
  editRiskItem,
  deleteRiskItem,
  listRiskLevels,
  addRiskLevel,
  editRiskLevel,
  removeRiskLevel,
} from "@/api/modules/anticheat/exceptionConfig";

export default {
  name: "ExceptionBehaviorConfigPage",
  components: { CommonFilterTableCard, InterviewerPageTabs },
  data() {
    return {
      activeTab: "monitor",
      monitorPage: { pageNo: 1, pageSize: 10 },
      monitorFilters: { behavior_desc: "", weight: "" },
      monitorFilterSchema: [
        { label: "监控项", field: "behavior_desc", type: "input", placeholder: "请输入" },
        {
          label: "权重",
          field: "weight",
          type: "select",
          options: [
            { label: "全部", value: "" },
            { label: "高", value: "1" },
            { label: "中", value: "2" },
            { label: "低", value: "3" },
          ],
        },
      ],
      monitorTotal: 0,
      monitorConfigs: [],
      monitorLoading: false,

      riskLevelConfigs: [],
      riskLoading: false,

      // 风险等级列表分页 / 筛选（前端过滤）
      riskPage: { pageNo: 1, pageSize: 10 },
      riskFilters: { search: "" },
      riskFilterSchema: [
        {
          label: "风险等级",
          field: "search",
          type: "input",
          placeholder: "请输入",
        },
      ],

      weightOptions: [
        { label: "高", value: 1 },
        { label: "中", value: 2 },
        { label: "低", value: 3 },
      ],
      riskCategoryOptions: [
        { label: "视觉监控", value: 1 },
        { label: "声音监控", value: 2 },
        { label: "屏幕与操作监控", value: 3 },
      ],

      // 风险等级弹窗
      riskDialogVisible: false,
      riskDialogMode: "create", // create | edit
      riskDialogEditingId: null,
      riskForm: { level_name: "", level_code: 0, min_score: 0, max_score: 0, description: "" },
      riskSubmitting: false,

      // 添加监控项弹窗
      behaviorAddDialogVisible: false,
      behaviorAddSubmitting: false,
      behaviorAddForm: { behaviorDesc: "", riskScore: 0, weight: 2, riskCategory: 1, sortOrder: 0, remarks: "" },

      // 编辑异常行为配置弹窗
      behaviorEditDialogVisible: false,
      behaviorEditSubmitting: false,
      behaviorEditForm: { behaviorDesc: "", riskScore: 0, weight: 2, riskCategory: 1, sortOrder: 0, remarks: "", id: null, action_type: "" },
    };
  },
  computed: {
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || "异常行为配置";
    },
    pageSub() {
      return (this.$route.meta && this.$route.meta.subTitle) || "";
    },

    canAddBehavior() {
      const codes =
        (this.$store &&
          this.$store.state &&
          this.$store.state.privilege &&
          this.$store.state.privilege.buttonCodes) ||
        [];
      // 权限未加载时：沿用原逻辑，避免页面不可用
      if (!codes || !codes.length) return this.isSystemAdmin;
      return this.isSystemAdmin && codes.includes("btn_action_add");
    },
    canAddRiskLevel() {
      const codes =
        (this.$store &&
          this.$store.state &&
          this.$store.state.privilege &&
          this.$store.state.privilege.buttonCodes) ||
        [];
      if (!codes || !codes.length) return true;
      return codes.includes("btn_risk_add");
    },
    canEditRiskLevel() {
      const codes =
        (this.$store &&
          this.$store.state &&
          this.$store.state.privilege &&
          this.$store.state.privilege.buttonCodes) ||
        [];
      if (!codes || !codes.length) return true;
      return codes.includes("btn_risk_edit");
    },
    canDeleteRiskLevel() {
      const codes =
        (this.$store &&
          this.$store.state &&
          this.$store.state.privilege &&
          this.$store.state.privilege.buttonCodes) ||
        [];
      if (!codes || !codes.length) return true;
      return codes.includes("btn_risk_delete");
    },
    monitorColumns() {
      return [
        { label: "监控项", slot: "behaviorDesc", minWidth: 200 },
        { label: "分类", slot: "riskCategory", width: 170 },
        { label: "风险分数", slot: "riskScore", width: 140 },
        { label: "权重", slot: "weight", width: 140 },
        { label: "排序", slot: "sortOrder", width: 120 },
        { label: "备注", slot: "remarks", minWidth: 200 },
        { label: "操作", slot: "actions", width: 180, fixed: "right" },
      ];
    },
    riskColumns() {
      return [
        { label: "风险等级", prop: "level_name", minWidth: 200 },
        { label: "等级排序", prop: "level_code", width: 140 },
        { label: "最小分", prop: "min_score", width: 120 },
        { label: "最大分", prop: "max_score", width: 120 },
        { label: "描述", prop: "description", minWidth: 220, slot: "description" },
        { label: "操作", slot: "actions", width: 180, fixed: "right" },
      ];
    },
    isSystemAdmin() {
      // 兼容：如果 userInfo 未下发（原型/未接后端），默认允许编辑，避免页面不可用。
      const u = this.$store && this.$store.state && this.$store.state.auth ? this.$store.state.auth.userInfo : null;
      if (!u) return true;

      const directBool = (v) => typeof v === "boolean" ? v : null;
      const directNum = (v) => (typeof v === "number" && Number.isFinite(v)) ? v : null;

      const candidates = [
        u.is_admin,
        u.isAdmin,
        u.is_system_admin,
        u.isSystemAdmin,
        u.admin,
        u.role,
        u.role_type,
        u.type,
        u.permissions,
      ];

      for (let i = 0; i < candidates.length; i++) {
        const v = candidates[i];
        const b = directBool(v);
        if (b != null) return b;
        const n = directNum(v);
        if (n != null) return n === 1;
        if (typeof v === "string") {
          const s = v.toLowerCase();
          if (s.includes("admin") || s.includes("system") && s.includes("admin")) return true;
        }
      }

      // permissions 可能是数组/字符串
      if (Array.isArray(u.permissions)) {
        return u.permissions.some((p) => String(p).toLowerCase().includes("admin"));
      }
      if (typeof u.permissions === "string") {
        return u.permissions.toLowerCase().includes("admin");
      }

      return false;
    },

    riskFormRules() {
      return {
        level_name: [
          {
            validator: (rule, value, callback) => {
              const s = String(value || "").trim();
              if (!s) return callback(new Error("请输入风险等级名称"));
              if (s.length > 50) return callback(new Error("风险等级名称不超过 50 个字符"));
              return callback();
            },
            trigger: "blur",
          },
        ],
        level_code: [
          {
            validator: (rule, value, callback) => {
              if (value === "" || value === null || value === undefined) return callback(new Error("请输入等级排序"));
              const n = Number(value);
              if (!Number.isFinite(n) || !Number.isInteger(n)) return callback(new Error("等级排序需为非负整数"));
              if (n < 0) return callback(new Error("等级排序需为非负整数"));
              return callback();
            },
            trigger: "blur",
          },
        ],
        min_score: [
          {
            validator: (rule, value, callback) => {
              if (value === "" || value === null || value === undefined) return callback(new Error("请输入最小分"));
              const n = Number(value);
              if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return callback(new Error("最小分需为非负整数"));
              return callback();
            },
            trigger: "blur",
          },
        ],
        max_score: [
          {
            validator: (rule, value, callback) => {
              if (value === "" || value === null || value === undefined) return callback(new Error("请输入最大分"));
              const n = Number(value);
              if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return callback(new Error("最大分需为非负整数"));
              const min = Number(this.riskForm && this.riskForm.min_score);
              if (Number.isFinite(min) && n < min) return callback(new Error("最大分需大于等于最小分"));
              return callback();
            },
            trigger: "blur",
          },
        ],
        description: [
          {
            validator: (rule, value, callback) => {
              const s = String(value || "");
              if (s.length > 500) return callback(new Error("描述不超过 500 个字符"));
              return callback();
            },
            trigger: "blur",
          },
        ],
      };
    },

    behaviorAddFormRules() {
      return {
        behaviorDesc: [
          {
            validator: (rule, value, callback) => {
              if (!String(value || "").trim()) {
                callback(new Error("请输入监控项"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        riskScore: [
          {
            validator: (rule, value, callback) => {
              const score = Number(value);
              if (value === "" || value === null || value === undefined) {
                callback(new Error("请输入风险分数"));
              } else if (!Number.isFinite(score) || !Number.isInteger(score) || score < 0 || score > 100) {
                callback(new Error("风险分数须为 0～100 之间的整数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        weight: [
          {
            validator: (rule, value, callback) => {
              if (![1, 2, 3].includes(Number(value))) {
                callback(new Error("请选择权重"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
        riskCategory: [
          {
            validator: (rule, value, callback) => {
              if (![1, 2, 3].includes(Number(value))) {
                callback(new Error("请选择分类"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
        sortOrder: [
          {
            validator: (rule, value, callback) => {
              if (value === "" || value === null || value === undefined) {
                callback(new Error("请输入排序"));
                return;
              }
              const n = Number(value);
              if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
                callback(new Error("排序需为非负整数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
      };
    },
  },
  created() {
    this.fetchMonitorConfigs(this.monitorPage.pageNo);
    this.fetchRiskLevels();
  },
  methods: {
    filterRiskLevels(items, ctx) {
      const list = Array.isArray(items) ? items : [];
      const filters = (ctx && ctx.filters) || {};
      const kw = String(filters.search || "")
        .trim()
        .toLowerCase();
      if (!kw) return list;

      return list.filter((row) => {
        if (!row) return false;
        const name = (row.level_name || row.levelName || "").toString().toLowerCase();
        const desc = (row.description || row.desc || "").toString().toLowerCase();
        return name.includes(kw) || desc.includes(kw);
      });
    },
    formatRiskScoreText(row) {
      if (!row) return "";
      const v = row.riskScore;
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      if (!Number.isFinite(n)) return "";
      return String(n);
    },
    formatWeightText(row) {
      if (!row) return "";
      const w = row.weight;
      const n = Number(w);
      if (n === 1) return "高";
      if (n === 2) return "中";
      if (n === 3) return "低";
      if (w == null || String(w).trim() === "") return "";
      return String(w);
    },
    formatRiskCategoryText(row) {
      if (!row) return "";
      const v = row.riskCategory;
      const n = Number(v);
      if (n === 1) return "视觉监控";
      if (n === 2) return "声音监控";
      if (n === 3) return "屏幕与操作监控";
      if (v == null || String(v).trim() === "") return "";
      return String(v);
    },
    formatSortOrderText(row) {
      if (!row) return "";
      const n = Number(row.sortOrder);
      if (!Number.isFinite(n)) return "";
      return String(n);
    },
    /** 列表/编辑统一为数值：1 高、2 中、3 低 */
    normalizeWeight(raw) {
      const n = Number(raw);
      if (n === 1 || n === 2 || n === 3) return n;
      const s = String(raw || "").trim();
      if (!s) return 2;
      const map = {
        High: 1,
        Medium: 2,
        Low: 3,
        high: 1,
        medium: 2,
        low: 3,
        高: 1,
        中: 2,
        低: 3,
      };
      if (map[s] != null) return map[s];
      const compact = s.replace(/\s+/g, "");
      if (map[compact] != null) return map[compact];
      return 2;
    },
    normalizeRiskCategory(raw) {
      const n = Number(raw);
      if (n === 1 || n === 2 || n === 3) return n;
      const s = String(raw || "").trim();
      if (!s) return 1;
      const map = {
        视觉监控: 1,
        声音监控: 2,
        屏幕与操作监控: 3,
      };
      if (map[s] != null) return map[s];
      return 1;
    },
    normalizeBehaviorRow(item) {
      if (!item) return null;
      const id = item.id ?? item.userid ?? item.user_id ?? item.behavior_id ?? item.monitor_id;
      const behaviorDescRaw =
        item.behavior_desc != null && String(item.behavior_desc).trim() !== ""
          ? String(item.behavior_desc)
          : "";
      const behaviorDesc =
        behaviorDescRaw ||
        (item.monitor_item != null ? String(item.monitor_item) : "") ||
        (item.monitorItem != null ? String(item.monitorItem) : "") ||
        (item.monitor_item_name != null ? String(item.monitor_item_name) : "") ||
        (item.monitorItemName != null ? String(item.monitorItemName) : "") ||
        (item.name != null ? String(item.name) : "") ||
        (item.item_name != null ? String(item.item_name) : "") ||
        (item.item != null ? String(item.item) : "") ||
        "";
      const riskScore = item.risk_score ?? item.riskScore ?? item.score ?? item.risk_point ?? 0;
      const weight = this.normalizeWeight(item.weight ?? item.risk_weight ?? item.weight_level ?? item.riskLevelWeight ?? 2);
      const riskCategory = this.normalizeRiskCategory(item.risk_category ?? item.riskCategory ?? item.category ?? 1);
      const sortOrder = Number(item.sort_order ?? item.sortOrder ?? 0) || 0;
      const remarks = item.remarks != null && item.remarks !== undefined ? String(item.remarks) : "";
      const action_type = item.action_type ?? item.actionType ?? "";
      return {
        id,
        behaviorDesc,
        riskScore: Number(riskScore) || 0,
        weight,
        riskCategory,
        sortOrder,
        remarks,
        action_type,
      };
    },
    normalizeRiskRow(item) {
      if (!item) return null;
      const id = item.risk_level_id ?? item.id ?? item.level_id ?? null;
      const level_name = item.level_name ?? item.risk_level ?? item.levelName ?? item.riskLevel ?? "";
      const level_code = Number(item.level_code ?? item.levelCode ?? item.sort_code ?? item.level_code_sort ?? 0) || 0;
      const min_score = Number(item.min_score ?? item.minScore ?? item.min ?? 0) || 0;
      const max_score = Number(item.max_score ?? item.maxScore ?? item.max ?? 0) || 0;
      const description = item.description ?? item.desc ?? item.risk_description ?? "";
      return {
        id,
        level_name: level_name ? String(level_name) : "",
        level_code,
        min_score,
        max_score,
        description: description != null ? String(description) : "",
      };
    },

    canEditBehaviorRow(row) {
      if (!row || row.id == null) return false;

      const codes =
        (this.$store &&
          this.$store.state &&
          this.$store.state.privilege &&
          this.$store.state.privilege.buttonCodes) ||
        [];
      // 权限未加载时：沿用原逻辑（可编辑），避免页面不可用
      if (!codes || !codes.length) return true;
      return codes.includes("btn_action_edit");
    },

    onMonitorFilterChange() {
      this.monitorPage.pageNo = 1;
      this.fetchMonitorConfigs(1);
    },

    async fetchMonitorConfigs(pageNum) {
      this.monitorLoading = true;
      try {
        if (pageNum != null) {
          this.monitorPage.pageNo = parseInt(pageNum, 10) || 1;
        }
        this.monitorPage.pageSize = parseInt(this.monitorPage.pageSize, 10) || 10;
        const behaviorDesc = (this.monitorFilters.behavior_desc || "").trim();
        const w = this.monitorFilters.weight;
        const weight = w !== "" && w != null ? Number(w) : null;

        const params = {
          page_num: this.monitorPage.pageNo,
          page_size: this.monitorPage.pageSize,
          behavior_desc: behaviorDesc || "",
        };
        if (weight != null && Number.isFinite(weight)) params.weight = weight;

        const res = await listRiskItems(params);

        if (Array.isArray(res)) {
          this.monitorConfigs = res.map((it) => this.normalizeBehaviorRow(it)).filter(Boolean);
          this.monitorTotal = res.length;
        } else {
          const rows = Array.isArray(res && res.rows) ? res.rows : [];
          this.monitorConfigs = rows.map((it) => this.normalizeBehaviorRow(it)).filter(Boolean);
          this.monitorTotal = typeof (res && res.total) === "number" ? res.total : 0;
        }
      } catch (e) {
        this.monitorConfigs = [];
        this.monitorTotal = 0;
      } finally {
        this.monitorLoading = false;
      }
    },

    async commitMonitorRow(row) {
      if (!this.canEditBehaviorRow(row)) return;
      if (this.monitorLoading) return;
      const risk_score = Number(row.riskScore);
      if (!Number.isFinite(risk_score)) {
        this.$message.warning("风险分数需为数字");
        return;
      }

      try {
        this.monitorLoading = true;
        const action_type = String(row.action_type ?? row.actionType ?? "").trim();
        if (!action_type) {
          this.$message.warning("缺少 action_type，无法更新");
          return;
        }
        await editRiskItem(action_type, {
          behavior_desc: String(row.behaviorDesc || "").trim(),
          risk_category: String(row.riskCategory),
          risk_score,
          sort_order: Number(row.sortOrder),
          remarks: row.remarks != null ? String(row.remarks) : "",
          // 后端约定：weight 字段值以字符串传输
          weight: String(Number(row.weight)),
        });
        this.$message.success("异常行为配置已更新");
        await this.fetchMonitorConfigs(this.monitorPage.pageNo);
      } catch (e) {
      } finally {
        this.monitorLoading = false;
      }
    },

    openAddBehaviorDialog() {
      this.behaviorAddForm = { behaviorDesc: "", riskScore: 0, weight: 2, riskCategory: 1, sortOrder: 0, remarks: "" };
      this.behaviorAddDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.behaviorAddFormRef) this.$refs.behaviorAddFormRef.clearValidate();
      });
    },
    closeBehaviorAddDialog() {
      this.behaviorAddDialogVisible = false;
      this.$nextTick(() => {
        if (this.$refs.behaviorAddFormRef) this.$refs.behaviorAddFormRef.clearValidate();
      });
    },
    confirmBehaviorAddDialog() {
      const form = this.$refs.behaviorAddFormRef;
      if (!form) return;
      form.validate((valid) => {
        if (!valid) return;
        this.submitBehaviorAddDialog();
      });
    },
    async submitBehaviorAddDialog() {
      this.behaviorAddSubmitting = true;
      try {
        await addBehaviorConfig({
          behavior_desc: String(this.behaviorAddForm.behaviorDesc).trim(),
          risk_score: Number(this.behaviorAddForm.riskScore),
          // 后端约定：risk_category / weight 字段值以字符串传输
          risk_category: String(this.behaviorAddForm.riskCategory),
          remarks: String(this.behaviorAddForm.remarks || "").trim(),
          sort_order: Number(this.behaviorAddForm.sortOrder),
          weight: String(this.behaviorAddForm.weight),
        });
        this.$message.success("添加异常行为配置成功");
        this.closeBehaviorAddDialog();
        this.monitorPage.pageNo = 1;
        await this.fetchMonitorConfigs(1);
      } catch (e) {
      } finally {
        this.behaviorAddSubmitting = false;
      }
    },

    async fetchRiskLevels() {
      this.riskLoading = true;
      try {
        const res = await listRiskLevels({});
        const rows = Array.isArray(res)
          ? res
          : Array.isArray(res && res.rows)
            ? res.rows
            : Array.isArray(res && res.data)
              ? res.data
              : [];
        const normalized = rows.map((it) => this.normalizeRiskRow(it)).filter(Boolean);
        if (!normalized.length) {
          // 默认等级（仅用于展示/原型），真实阈值建议后端返回
          this.riskLevelConfigs = [
            { id: null, level_name: "高风险", level_code: 0, min_score: 80, max_score: 100, description: "高风险" },
            { id: null, level_name: "中风险", level_code: 1, min_score: 50, max_score: 79, description: "中风险" },
            { id: null, level_name: "低风险", level_code: 2, min_score: 0, max_score: 49, description: "低风险" },
          ];
        } else {
          // 按等级排序展示（level_code 越小越靠前）
          this.riskLevelConfigs = normalized.sort((a, b) => Number(a.level_code) - Number(b.level_code));
        }
      } catch (e) {
        this.riskLevelConfigs = [];
      } finally {
        this.riskLoading = false;
      }
    },

    openRiskLevelDialog(mode, row = null) {
      this.riskDialogMode = mode;

      if (mode === "edit") {
        const id = row && row.id;
        if (id == null) {
          this.$message.warning("默认等级暂不支持编辑（请先从后端配置并下发 id）");
          return;
        }
        this.riskDialogEditingId = id;
        this.riskForm = {
          level_name: String(row.level_name || row.levelName || ""),
          level_code: Number(row.level_code) || 0,
          min_score: Number(row.min_score) || 0,
          max_score: Number(row.max_score) || 0,
          description: row.description != null ? String(row.description) : "",
        };
      } else {
        this.riskDialogEditingId = null;
        this.riskForm = { level_name: "", level_code: 0, min_score: 0, max_score: 0, description: "" };
      }
      this.riskDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.riskFormRef) this.$refs.riskFormRef.clearValidate();
      });
    },
    closeRiskDialog() {
      this.riskDialogVisible = false;
      this.riskSubmitting = false;
      this.riskDialogEditingId = null;
      this.$nextTick(() => {
        if (this.$refs.riskFormRef) this.$refs.riskFormRef.clearValidate();
      });
    },
    confirmRiskDialog() {
      const form = this.$refs.riskFormRef;
      if (!form) return;
      form.validate((valid) => {
        if (!valid) return;
        this.submitRiskDialog();
      });
    },
    async submitRiskDialog() {
      this.riskSubmitting = true;
      try {
        if (this.riskDialogMode === "edit") {
          await editRiskLevel({
            risk_level_id: this.riskDialogEditingId,
            level_name: String(this.riskForm.level_name).trim(),
            level_code: Number(this.riskForm.level_code),
            min_score: Number(this.riskForm.min_score),
            max_score: Number(this.riskForm.max_score),
            description: String(this.riskForm.description || "").trim(),
          });
          this.$message.success("风险等级已更新");
        } else {
          await addRiskLevel({
            level_name: String(this.riskForm.level_name).trim(),
            level_code: Number(this.riskForm.level_code),
            min_score: Number(this.riskForm.min_score),
            max_score: Number(this.riskForm.max_score),
            description: String(this.riskForm.description || "").trim(),
          });
          this.$message.success("风险等级添加成功");
        }
        this.closeRiskDialog();
        await this.fetchRiskLevels();
      } catch (e) {
      } finally {
        this.riskSubmitting = false;
      }
    },
    async deleteRiskLevel(row) {
      if (!this.canDeleteRiskLevel) return;
      if (!row || row.id == null) {
        this.$message.warning("默认等级暂不支持删除（请先从后端配置并下发 id）");
        return;
      }
      try {
        await this.$confirm("确定要删除该风险等级吗？", "提示", { type: "warning" });
      } catch (e) {
        return;
      }
      try {
        await removeRiskLevel({ risk_level_id: row.id });
        this.$message.success("风险等级已删除");
        await this.fetchRiskLevels();
      } catch (e) {
      }
    },

    openBehaviorEditDialog(row) {
      if (!row) return;
      this.behaviorEditForm = {
        id: row.id ?? null,
        behaviorDesc: String(row.behaviorDesc || ""),
        riskScore: Number(row.riskScore) || 0,
        weight: Number(row.weight) || 2,
        riskCategory: Number(row.riskCategory) || 1,
        sortOrder: Number(row.sortOrder) || 0,
        remarks: row.remarks != null ? String(row.remarks) : "",
        action_type: String(row.action_type ?? row.actionType ?? "").trim(),
      };
      this.behaviorEditDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.behaviorEditFormRef) this.$refs.behaviorEditFormRef.clearValidate();
      });
    },
    closeBehaviorEditDialog() {
      this.behaviorEditDialogVisible = false;
      this.behaviorEditSubmitting = false;
      this.$nextTick(() => {
        if (this.$refs.behaviorEditFormRef) this.$refs.behaviorEditFormRef.clearValidate();
      });
    },
    confirmBehaviorEditDialog() {
      const form = this.$refs.behaviorEditFormRef;
      if (!form) return;
      form.validate((valid) => {
        if (!valid) return;
        this.submitBehaviorEditDialog();
      });
    },
    async submitBehaviorEditDialog() {
      this.behaviorEditSubmitting = true;
      try {
        const action_type = String(this.behaviorEditForm.action_type ?? "").trim();
        if (!action_type) {
          this.$message.warning("缺少 action_type，无法更新");
          return;
        }
        await editRiskItem(action_type, {
          behavior_desc: String(this.behaviorEditForm.behaviorDesc || "").trim(),
          risk_category: String(this.behaviorEditForm.riskCategory),
          risk_score: Number(this.behaviorEditForm.riskScore),
          sort_order: Number(this.behaviorEditForm.sortOrder),
          remarks: String(this.behaviorEditForm.remarks || "").trim(),
          weight: String(Number(this.behaviorEditForm.weight)),
        });
        this.$message.success("异常行为配置已更新");
        this.closeBehaviorEditDialog();
        await this.fetchMonitorConfigs(this.monitorPage.pageNo);
      } catch (e) {
      } finally {
        this.behaviorEditSubmitting = false;
      }
    },

    async deleteBehaviorConfig(row) {
      if (!row) return;
      const behavior_desc = String(row.behaviorDesc || "").trim();
      const risk_category = String(row.riskCategory);
      const action_type = String(row.action_type ?? row.actionType ?? "").trim();
      if (!behavior_desc || !risk_category) {
        this.$message.warning("缺少必要字段，无法删除");
        return;
      }
      if (!action_type) {
        this.$message.warning("缺少 action_type，无法删除");
        return;
      }

      try {
        await this.$confirm("确定要删除该异常行为配置吗？", "提示", { type: "warning" });
      } catch (e) {
        return;
      }

      try {
        await deleteRiskItem(action_type, { behavior_desc, risk_category });
        this.$message.success("异常行为配置已删除");
        await this.fetchMonitorConfigs(this.monitorPage.pageNo);
      } catch (e) {
      }
    },
  },
};
</script>

<style scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  height: 33px;
}

.topTitle {
  font-size: 18px;
  font-weight: 900;
}

.topSub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.topRight {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.topActionPlus {
  display: inline-block;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  transform: scale(1.35);
  transform-origin: center;
  margin-right: 4px;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.dialog-form-sheet .el-form-item {
  margin-bottom: 10px;
}

.dialog-form-sheet .el-form-item__label {
  margin-bottom: 6px;
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .el-dialog__footer {
  display: flex;
  justify-content: flex-end;
}

::v-deep .el-dialog__body {
  padding-top: 0;
  padding-bottom: 0;
}

::v-deep .el-form-item__label {
  padding-top: 0;
  padding-bottom: 0;
}

.remarks-readonly {
  display: inline-block;
  max-width: 100%;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  font-size: 13px;
  color: #606266;
}

/* 编辑异常行为配置：监控项不可修改（置灰） */
.behavior-desc-disabled ::v-deep .el-input__inner {
  background: #f5f7fa;
  color: #606266;
  cursor: not-allowed;
}
</style>

